// Publish Training, the one flow for building a course with Elliott: Details ->
// Goals -> Format -> Review -> Structure -> Content -> Members. The first four
// are here: one centred column per step, one input beneath the next, the
// stepper above and the bottom bar (status and actions) always in view. Roles
// and capabilities come from Capability Setup. The last three are the course
// wizard's own steps (./TrainingBuildSteps.tsx).
//
// The program is created when Details is complete, so it has a card on the
// dashboard (in Design) from then on. What the admin has entered is kept with
// it (./trainingSnapshot.ts), and Continue on the card reopens the flow at the
// step they left.
//
// The program type decides what it is built on (see ./trainingRules.ts): Role
// Excellence builds the capabilities its roles share, with any other learning
// outcomes beside them; Development and Standards, and a Role Excellence
// program with no capability, are built on at least three learning outcomes.
//
// Elliott's Pick of format and Elliott's preview come from the backend
// (POST /v3/admin/capability/trainings/format and …/preview).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Box, Collapse, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { apiMessage, capabilityApi, type TrainingInput } from '../../../../../api/capability';
import { useCapabilityOrg } from '../../../../../queries/useCapability';
import { Bone, fadeInSx } from '../../../../../components/ui/DashboardSkeletons';
import type { DrawerCreateProgramInput } from '../../Program/components/learning-lifecycle/useAdminLearningLifecycle';
import { useOrgAudienceOptions } from '../../Program/components/learning-lifecycle/useOrgMemberAttributes';
import { withCountedCapabilities, type SetupRole } from '../capabilitySetup';
import { isRoleUsable, type CapabilitySet } from '../sharedSets';
import {
  ActionButton,
  BODY,
  BRANDY,
  BRANDY_FILL,
  BRANDY_FRAME,
  BRANDY_TINT,
  FONT,
  FlowStep,
  FormRow,
  INPUT_SX,
  LABEL,
  LINE,
  LINE_2,
  SECTION_TITLE,
  StepDot,
  Stepper,
  TEXT_2,
  TEXT_3,
  TEXT_4,
  TEXTAREA_SX,
  WELL
} from '../designSystem';
import {
  CATEGORY_NAME,
  DETAIL_FIELDS,
  DetailsFields,
  EMPTY_DETAILS,
  detailsMissing,
  stepBar,
  type DetailKey,
  type DetailsFieldProps,
  type FlowDetails,
  type SetDetail
} from './DetailsStep';
import {
  allRoleIds,
  fromServerGroups,
  loadGroups,
  readAssessRoleIds,
  saveGroups,
  type AudienceGroupsDraft
} from './audienceGroups';
import { useRequirementsPilot } from '../../Program/components/program tabs/ProgramSettings/requirements/useRequirements';
import { audienceRequirementsBackend } from '../../Program/components/program tabs/ProgramSettings/requirements/flags';
import { API } from '../../../../../api';
import { audienceGroupsRefusalOf, type AudienceGroupsRefusal } from '../../../../../api/audienceRequirements';
import { getLLAuthHeaders } from '../../../utils/llAuthHeaders';
import { CapabilitiesField, ReviewFields, formatDate, listNames, useElliottPreview, type SummaryLine } from './common';
import TrainingBuildSteps from './TrainingBuildSteps';
import { openTraining, saveForm, startBuild, syncProgram } from './trainingBuild';
import { MIN_OUTCOMES, capabilitiesForType, lessonsLock, sourceForType, type FormatKey } from './trainingRules';
import { SOURCE_LABEL } from '../capabilitySource';
import {
  BUILD_STEPS,
  FORM_STEPS,
  detailsOf,
  nameTaken,
  programFieldsOf,
  snapshotOf,
  type FormStep,
  type ProgramFields,
  type TrainingRecommendation,
  type TrainingSnapshot
} from './trainingSnapshot';

interface FormatInfo {
  key: FormatKey;
  title: string;
  length: string;
  job: string;
  detail: [string, string][];
}

/**
 * The three formats, described for the program being built. Roleplays assess
 * capabilities, so only a program that builds capabilities has them; one built
 * on learning outcomes alone is assessed with Quiz Questions.
 */
const formatsFor = (withCapabilities: boolean): FormatInfo[] =>
  withCapabilities
    ? [
        {
          key: 'course',
          title: 'Course',
          length: '12-16 lessons, about 75 minutes',
          job: 'Teach capabilities in depth.',
          detail: [
            ['Best For', 'Building several capabilities, or one that takes several lessons to do well.'],
            [
              'Each Lesson',
              'A Lesson Body and Quiz Questions on one capability. Each capability ends with a Roleplay scored against its levels. A capability with four or more lessons has two.'
            ],
            ['Pace', 'Self-paced over one to two weeks.']
          ]
        },
        {
          key: 'shortCourse',
          title: 'Short Course',
          length: '2-4 lessons, 10-20 minutes',
          job: 'Teach a few capabilities quickly.',
          detail: [
            ['Best For', 'Sharpening up to four capabilities people already use.'],
            ['Each Lesson', 'A Lesson Body, Quiz Questions and one scored Roleplay, on a single capability.'],
            ['Pace', 'One sitting, or a lesson a day.']
          ]
        },
        {
          key: 'brief',
          title: 'Brief',
          length: '1 lesson, under 5 minutes',
          job: 'Refresh one capability.',
          detail: [
            ['Best For', 'A single capability people need a quick refresher on.'],
            ['The Lesson', 'A short Lesson Body with Quiz Questions and one scored Roleplay.'],
            ['Pace', 'Read once, in a few minutes.']
          ]
        }
      ]
    : [
        {
          key: 'course',
          title: 'Course',
          length: '12-16 lessons, about 75 minutes',
          job: 'Teach a subject in depth.',
          detail: [
            ['Best For', 'Several goals that take time to cover well.'],
            ['Each Lesson', 'A Lesson Body and Quiz Questions on your goals.'],
            ['Pace', 'Self-paced over one to two weeks.']
          ]
        },
        {
          key: 'shortCourse',
          title: 'Short Course',
          length: '2-4 lessons, 10-20 minutes',
          job: 'Teach one narrow thing quickly.',
          detail: [
            ['Best For', 'A few goals: a tool, a process, a refresh.'],
            ['Each Lesson', 'A Lesson Body and Quiz Questions on your goals. No Roleplays.'],
            ['Pace', 'One sitting, or a lesson a day.']
          ]
        },
        {
          key: 'brief',
          title: 'Brief',
          length: '1 lesson, under 5 minutes',
          job: 'Tell people one thing.',
          detail: [
            ['Best For', 'An update, a reminder or a change people need to know about.'],
            ['The Lesson', 'A short Lesson Body with Quiz Questions. No Roleplays.'],
            ['Pace', 'Read once, in a few minutes.']
          ]
        }
      ];

const WHY_PICKED = 'Why Elliott recommends this';

/** Learning Outcomes always shows at least this many lines. */
const OUTCOME_ROWS = 3;

type GoalKey = 'purpose' | 'capabilities' | 'outcomes';
type ReviewKey = DetailKey | GoalKey | 'format';

/** What Publish Training opens filled in with, from a recommended course. */
export interface TrainingSeed {
  name: string;
  stageId: string | null;
  audienceIds: string[];
  purpose: string;
  /** A recommendation for one role on one capability: the role, the category and the capability it is for. */
  roleIds?: string[];
  category?: FlowDetails['category'];
  capabilityIds?: string[];
  /** A Guided Course: built exactly as a Course is, and kept with the program as one. */
  guided?: boolean;
  /** The capability recommendation it comes from, kept with the program until its build starts. */
  recommendation?: TrainingRecommendation;
}

/** The program the flow builds, where the organization's programs are kept. */
export interface TrainingProgram {
  /**
   * Creates the program. Resolves its id, or null when it could not be created: the reason has been
   * shown, except the audience groups refused for someone in both (`refusal`), shown beside the groups.
   */
  create: (input: DrawerCreateProgramInput) => Promise<{ id: string | null; refusal?: AudienceGroupsRefusal }>;
  /** The names already in use by the organization's programs. */
  existingNames: string[];
  /** The program (by id) was created, or moved on: whatever lists programs should read them again. */
  changed?: (groupId: string) => void;
}

interface TrainingFlowProps {
  pillars: { id: string; name: string }[];
  members: { id: string; name: string }[];
  /** Every role set up in Capability Setup. */
  roles: SetupRole[];
  /** The organization's shared capabilities (Development, Standards), applied to roles. */
  sets?: CapabilitySet[];
  /** The roles are still being read: nothing is shown until they are, so nothing is built without them. */
  rolesLoading?: boolean;
  teamsListId: string | null;
  onClose: () => void;
  program: TrainingProgram;
  /** What the flow opens filled in with (a recommended course). */
  seed?: TrainingSeed;
  /** A program built in this flow, reopened where it was left (Continue on its card). Published: only Members is left. */
  resume?: { groupId: string; published?: boolean } | null;
  /** Structure has been asked for: the Training is under way (with the recommendation it comes from, if any). */
  onGenerate?: (recommendation: TrainingRecommendation | null) => void;
  /** Invite & Finish: the program is built and its people invited. */
  onFinished?: (groupId: string) => void;
}

/** The lines of Learning Outcomes for these outcomes: never fewer than the page shows. */
const outcomeLines = (given: string[]) => [...given, ...Array(Math.max(0, OUTCOME_ROWS - given.length)).fill('')];

/**
 * The flow, once it knows what it opens on: nothing, a recommended course
 * (`seed`), or a program reopened (`kept`, with its id in `resume`).
 */
const TrainingSteps: React.FC<
  TrainingFlowProps & {
    kept: TrainingSnapshot | null;
    /** The audience groups the program holds, read from the backend when it keeps them (null: none yet). */
    heldGroups?: AudienceGroupsDraft | null;
  }
> = ({
  pillars,
  members,
  roles,
  sets = [],
  teamsListId,
  onClose,
  program,
  seed,
  resume,
  onGenerate,
  onFinished,
  kept,
  heldGroups = null
}) => {
  const org = useCapabilityOrg();
  // Audience Requirements (prototype: The Playground, development builds): Read & Assess and Read Only groups.
  const groupsOn = useRequirementsPilot();
  // With its backend on, the groups are the program's audience, kept by the backend (DUMMY until then: this browser).
  const groupsOnBackend = groupsOn && audienceRequirementsBackend();
  const [step, setStep] = useState<FormStep>(kept && kept.step !== 'build' ? kept.step : kept ? 'review' : 'details');
  // The build (Structure, Content, Members) takes over from the form once Structure is asked for.
  const [phase, setPhase] = useState<'form' | 'build'>(kept?.step === 'build' ? 'build' : 'form');
  const [details, setDetails] = useState<FlowDetails>(() => {
    if (!kept) {
      return {
        ...EMPTY_DETAILS,
        ...(seed
          ? {
              name: seed.name,
              stageId: seed.stageId,
              audienceIds: seed.audienceIds,
              roleIds: seed.roleIds ?? [],
              category: seed.category ?? null
            }
          : null)
      };
    }
    // The pilot reads a program's groups: from the backend once it keeps them, else (DUMMY) from this browser,
    // where an older snapshot may still carry them. Anywhere else Details is as it always was.
    const { groups: sent, ...rest } = detailsOf(kept);
    const dummyGroups = () => (resume?.groupId ? loadGroups(resume.groupId) : null) ?? sent;
    const groups = groupsOnBackend ? heldGroups ?? undefined : groupsOn ? dummyGroups() : undefined;
    return groups ? { ...rest, groups } : rest;
  });
  const [purpose, setPurpose] = useState(kept?.purpose ?? seed?.purpose ?? '');
  const [outcomes, setOutcomes] = useState<string[]>(() => outcomeLines(kept?.outcomes ?? []));
  // null = every capability of the roles in scope, until the manager unticks one.
  const [capabilityIds, setCapabilityIds] = useState<string[] | null>(
    kept ? kept.capabilityIds : seed?.capabilityIds ?? null
  );
  const [formatChoice, setFormatChoice] = useState<FormatKey | null>(kept?.format ?? null);
  const [expanded, setExpanded] = useState<FormatKey[] | null>(null);

  const set: SetDetail = (key, value) => setDetails((d) => ({ ...d, [key]: value }));
  const at = FORM_STEPS.indexOf(step);
  const stepper = <Stepper steps={BUILD_STEPS} current={at} />;
  const { labelById, catalog } = useOrgAudienceOptions();

  // The program: created on first leaving Details, then sent whatever Details changes.
  const [groupId, setGroupId] = useState<string | null>(resume?.groupId ?? null);
  const [busy, setBusy] = useState(false);
  // DUMMY: the designer's Audience Requirements reads the groups from this browser (see audienceGroups.ts),
  // until the backend keeps them.
  useEffect(() => {
    if (groupsOn && !groupsOnBackend && groupId && details.groups) saveGroups(groupId, details.groups);
  }, [groupsOn, groupsOnBackend, groupId, details.groups]);
  // The backend's refusal of the groups last sent, shown beside them until they change.
  const [groupsRefusal, setGroupsRefusal] = useState<AudienceGroupsRefusal | null>(null);
  useEffect(() => setGroupsRefusal(null), [details.groups]);
  // What the program was last saved with. A program reopened is as its snapshot says it was last sent,
  // not as its Details read (they may have been edited on Review since); null when that is not known.
  const held = useRef<ProgramFields | null>(kept?.synced ?? null);
  const taken = nameTaken(details.name, program.existingNames, held.current?.name ?? kept?.details.name ?? null);
  /**
   * Save the program as it now is (`now`): created the first time, then sent what changed. Resolves
   * its id, or null when it could not be saved (the reason, a name already taken say, has been shown).
   */
  const keepProgram = async (now: ProgramFields): Promise<string | null> => {
    if (!groupId) {
      // Nobody is enrolled by creating it: that waits for Invite & Finish.
      // goalLessons: the backend builds every capability and learning outcome a lesson of its own (PRD R10).
      const { id, refusal } = await program.create({ ...now, awaitingInviteFinish: true, goalLessons: true });
      if (refusal) setGroupsRefusal(refusal);
      if (!id) return null;
      held.current = now;
      setGroupId(id);
      program.changed?.(id);
      return id;
    }
    try {
      if (await syncProgram(groupId, held.current, now)) program.changed?.(groupId);
      held.current = now;
      return groupId;
    } catch (err) {
      // Someone the groups would put in both: said beside them, not in a toast.
      const refusal = now.audienceGroups ? audienceGroupsRefusalOf(err) : null;
      if (refusal?.overlaps) setGroupsRefusal(refusal);
      else toast.error(apiMessage(err, 'Your changes could not be saved. Please try again.'));
      return null;
    }
  };

  // Every type builds the capabilities every chosen role shares, from its own source (the roles' own, or
  // the Development or Standards ones applied to them): everyone in the program then holds each one, so
  // everyone is graded on its Roleplays.
  // Each with what counts on it: a capability added since its levels were last confirmed waits for them.
  // With the groups on the backend, the program is for every role they name, and built for the Read & Assess
  // ones (the backend checks the capabilities against those alone). Otherwise both are Details' roles.
  const grouped = groupsOnBackend && details.groups ? details.groups : null;
  const audienceRoleIds = grouped ? allRoleIds(grouped) : details.roleIds;
  const buildRoleIds = grouped ? readAssessRoleIds(grouped) : details.roleIds;
  const chosenRoles = roles
    .filter((r) => buildRoleIds.includes(r.id) && isRoleUsable(r, sets))
    .map(withCountedCapabilities);
  const allCapabilities = capabilitiesForType(details.category, chosenRoles, sets);
  const sourceLabel =
    sourceForType(details.category) !== 'role' ? `${SOURCE_LABEL[sourceForType(details.category)]} ` : '';
  const picked = (capabilityIds ?? allCapabilities.map((c) => c.id)).filter((id) =>
    allCapabilities.some((c) => c.id === id)
  );
  const pickedNames = allCapabilities.filter((c) => picked.includes(c.id)).map((c) => c.name);
  const givenOutcomes = outcomes.map((o) => o.trim()).filter(Boolean);
  const withCapabilities = picked.length > 0;
  // With no capability to build, the program stands on its learning outcomes: at least three.
  const outcomesNeeded = withCapabilities ? 0 : MIN_OUTCOMES;
  const FORMATS = formatsFor(withCapabilities);
  // A format with no room for the goals chosen can't be picked, by the manager or by Elliott: every
  // capability and every learning outcome takes a lesson of its own.
  const lockOf = (key: FormatKey) => lessonsLock(key, picked.length + givenOutcomes.length);
  const usable = (key: FormatKey | null | undefined) => (key && !lockOf(key) ? key : null);

  const stageName = pillars.find((p) => p.id === details.stageId)?.name ?? null;
  const ownerName = members.find((m) => m.id === details.ownerId)?.name ?? null;
  const who = details.allLearners
    ? 'Everyone in the organization'
    : listNames([
        ...roles.filter((r) => audienceRoleIds.includes(r.id)).map((r) => r.name),
        ...(grouped ? [] : details.audienceIds.map((id) => labelById.get(id) ?? '').filter(Boolean))
      ]);

  // What Elliott picks the format and writes the preview from.
  const input: TrainingInput = {
    name: details.name.trim(),
    stage: stageName ?? undefined,
    category: details.category,
    purpose: purpose.trim(),
    audience: who,
    capabilities: allCapabilities
      .filter((c) => picked.includes(c.id))
      .map((c) => ({ name: c.name, levels: c.criteria })),
    learningOutcomes: givenOutcomes
  };
  const inputKey = JSON.stringify(input);

  // Elliott's Pick, asked for on reaching Format and again whenever what it is picked from changes.
  const [pick, setPick] = useState<{ key: string; format: FormatKey; reason: string } | null>(null);
  const [picking, setPicking] = useState(false);
  useEffect(() => {
    if (step !== 'format' || pick?.key === inputKey) return undefined;
    let alive = true;
    setPicking(true);
    capabilityApi
      .trainingFormat({ ...input, goalLessons: true }, org)
      .then((answer) => alive && setPick({ key: inputKey, ...answer }))
      // No pick: the manager chooses without one.
      .catch(() => alive && setPick(null))
      .finally(() => alive && setPicking(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, inputKey]);
  const format = usable(formatChoice) ?? usable(pick?.format) ?? null;
  const formatInfo = FORMATS.find((f) => f.key === format) ?? null;
  // Elliott's pick is open to start with; after that it is whatever the manager opened.
  const open = expanded ?? (usable(pick?.format) ? [pick?.format as FormatKey] : []);

  const preview = useElliottPreview(
    () => capabilityApi.trainingPreview({ ...input, format: format ?? undefined }, org).then((r) => r.text),
    step === 'review' && phase === 'form',
    kept?.preview ?? null
  );

  // What has been entered is kept with the program as each step is left, and on closing part-way
  // through one, so its card's Continue reopens here. Once the build starts, the wizard's own
  // saving carries it on (and must not be written over from here).
  const guided = kept ? Boolean(kept.guided) : Boolean(seed?.guided);
  const recommendation = kept ? kept.recommendation ?? null : seed?.recommendation ?? null;
  // The roles' names ride with their ids, for wherever the program's audience is shown without the roles to hand.
  const roleNames = roles.filter((r) => audienceRoleIds.includes(r.id)).map((r) => r.name);
  const settled = {
    details,
    purpose,
    outcomes: givenOutcomes,
    format,
    preview: preview.text,
    guided,
    roleNames,
    recommendation
  };
  const snapshot = snapshotOf({ ...settled, step, capabilityIds, synced: held.current });
  const latest = useRef(snapshot);
  latest.current = snapshot;
  const saved = useRef<string | null>(kept ? JSON.stringify(snapshot) : null);
  const groupRef = useRef(groupId);
  groupRef.current = groupId;
  const building = useRef(kept?.step === 'build');
  const persist = useCallback(() => {
    const id = groupRef.current;
    if (!id || building.current) return;
    const text = JSON.stringify(latest.current);
    if (text === saved.current) return;
    saved.current = text;
    saveForm(id, latest.current).catch(() => {
      saved.current = null;
      // Said, not waited on: closing goes ahead either way.
      toast.error('Your latest changes could not be saved. Please check your connection and try again.');
    });
  }, []);
  useEffect(persist, [persist, step, groupId]);
  useEffect(() => persist, [persist]);

  const go = (key: FormStep) => setStep(key);
  // Bottom left of the bar, apart from the actions on the right.
  const previous = <ActionButton onClick={() => go(FORM_STEPS[at - 1])}>Previous</ActionButton>;
  // What the program holds: Details, and what it is built from (all the roles share, until Goals says otherwise).
  const programNow = () =>
    programFieldsOf(details, catalog, { capabilityIds: picked, format, guided }, groupsOnBackend);
  const leaveDetails = async () => {
    setBusy(true);
    const id = await keepProgram(programNow());
    setBusy(false);
    if (id) go('goals');
  };
  const generate = async () => {
    setBusy(true);
    try {
      // Everything changed since, Review's edits among them, reaches the program before it is generated from.
      const id = await keepProgram(programNow());
      if (!id) return;
      building.current = true;
      // The generator reads the capabilities (all four levels) and the format from the program itself:
      // it is given only the other learning outcomes.
      await startBuild(
        id,
        snapshotOf({ ...settled, step: 'build', capabilityIds: picked, synced: held.current }),
        givenOutcomes
      );
      onGenerate?.(recommendation);
      program.changed?.(id);
      setPhase('build');
    } catch (err) {
      building.current = false;
      toast.error(apiMessage(err, 'Elliott could not start the build. Please try again.'));
    } finally {
      setBusy(false);
    }
  };
  const detailProps: DetailsFieldProps = {
    type: 'training',
    details,
    set,
    pillars,
    members,
    roles,
    teamsListId,
    sets,
    groupsRefusal
  };

  // ---- The inputs of Goals and Format, each on its own so Review can open one to edit.
  const purposeField = (
    <FormRow
      required
      label="What's the purpose of this program?"
      tip="What the Training is for, in a sentence or two. Elliott builds everything around this."
    >
      <Box
        component="textarea"
        rows={3}
        value={purpose}
        aria-label="Purpose"
        placeholder="e.g. Help new Account Executives handle pricing objections with confidence."
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setPurpose(e.target.value)}
        sx={TEXTAREA_SX}
      />
    </FormRow>
  );
  const capabilitiesField =
    allCapabilities.length > 0 ? (
      <CapabilitiesField
        // One list: what every chosen role shares.
        roles={[{ ...chosenRoles[0], capabilities: allCapabilities }]}
        // The type's source pill (Role, Development, Standards) heads the list, on the left.
        showSource
        picked={picked}
        onChange={setCapabilityIds}
        required={false}
        label={`Which ${sourceLabel}capabilities should this program focus on?`}
        help="Start with the biggest gaps. The fewer you choose, the more lessons and practice each one gets."
        tip="Each capability selected gets at least one dedicated lesson. Learning Outcomes added below get at least one dedicated lesson as well. This ensures all of your course goals are achieved."
      />
    ) : (
      <FormRow label="Capabilities">
        <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_3 }}>
          {chosenRoles.length > 1
            ? `The roles you chose share no ${sourceLabel}capabilities, so this program is built on goals only.`
            : `This role has no ${sourceLabel}capabilities yet, so this program is built on goals only.`}
        </Typography>
      </FormRow>
    );
  const outcomesField = (
    <FormRow
      required={outcomesNeeded > 0}
      label="Learning Outcomes"
      tip={
        withCapabilities
          ? 'Optional. Each Learning Outcome gets at least one dedicated lesson, assessed with Quiz Questions (Roleplays assess capabilities only). This ensures all of your course goals are achieved.'
          : `Learning Outcomes should only be used if your course does not relate to any capabilities. Each gets at least one dedicated lesson, assessed with Quiz Questions. At least ${MIN_OUTCOMES}.`
      }
      aside={
        outcomesNeeded > 0 && (
          <Typography sx={{ ...LABEL, color: TEXT_3 }}>
            {Math.min(givenOutcomes.length, outcomesNeeded)} of {outcomesNeeded} Required
          </Typography>
        )
      }
    >
      {outcomes.map((o, i) => (
        // The list is positional: lines are only ever edited in place or removed by index.
        // eslint-disable-next-line react/no-array-index-key
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <StepDot number={i + 1} state={o.trim() ? 'current' : 'todo'} />
          <Box
            component="input"
            value={o}
            aria-label={`Goal ${i + 1}`}
            placeholder={i === 0 ? 'e.g. Reframe a price objection around value' : 'Another goal'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setOutcomes((list) => list.map((x, j) => (j === i ? e.target.value : x)))
            }
            sx={INPUT_SX}
          />
          {outcomes.length > OUTCOME_ROWS && (
            <Box
              component="button"
              type="button"
              aria-label={`Remove goal ${i + 1}`}
              onClick={() => setOutcomes((list) => list.filter((_o, j) => j !== i))}
              sx={{
                width: 28,
                height: 28,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                borderRadius: '4px',
                bgcolor: 'transparent',
                color: TEXT_3,
                cursor: 'pointer',
                '&:hover': { color: '#FF6A55' }
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </Box>
          )}
        </Box>
      ))}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ActionButton onClick={() => setOutcomes((list) => [...list, ''])}>Add Goal</ActionButton>
      </Box>
    </FormRow>
  );
  const formatField = (
    <FormRow
      required
      label="Format"
      tip="How long the Training is. Elliott suggests one from the capabilities and outcomes you chose; open any to see what it involves."
    >
      <Box role="radiogroup" sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {FORMATS.map((f) => {
          const locked = lockOf(f.key);
          const on = f.key === format;
          const isOpen = open.includes(f.key);
          const isPick = !locked && f.key === pick?.format;
          return (
            <Box
              key={f.key}
              sx={{
                borderRadius: '4px',
                border: on ? BRANDY_FRAME : `1px solid ${LINE_2}`,
                bgcolor: on ? BRANDY_TINT : 'transparent',
                transition: 'border-color 0.15s, background-color 0.15s',
                '&:hover': { borderColor: locked ? LINE_2 : BRANDY }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {/* Choosing the format: everything but the arrow. */}
                <Box
                  component="button"
                  type="button"
                  role="radio"
                  aria-checked={on}
                  disabled={Boolean(locked)}
                  onClick={() => setFormatChoice(f.key)}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    minHeight: 40,
                    pl: '12px',
                    border: 'none',
                    bgcolor: 'transparent',
                    textAlign: 'left',
                    cursor: 'pointer',
                    // Locked: no room for the capabilities chosen. The reason takes the place of its length.
                    '&:disabled': { cursor: 'default', '& > *:not(:last-child)': { opacity: 0.45 } }
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      width: 14,
                      height: 14,
                      flexShrink: 0,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `1.5px solid ${on ? BRANDY : TEXT_4}`,
                      bgcolor: on ? BRANDY : 'transparent'
                    }}
                  >
                    {on && <CheckIcon sx={{ fontSize: 11, color: WELL }} />}
                  </Box>
                  <Typography sx={{ ...SECTION_TITLE, width: 116, fontSize: 13, fontWeight: on ? 600 : 400 }}>
                    {f.title}
                  </Typography>
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontFamily: FONT,
                      fontSize: 12.5,
                      color: locked ? '#FF6A55' : TEXT_2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {locked ?? f.length}
                  </Typography>
                  {isPick && (
                    <Box
                      sx={{
                        ...LABEL,
                        flexShrink: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        height: 20,
                        px: '6px',
                        borderRadius: '4px',
                        bgcolor: BRANDY_FILL,
                        fontSize: 9.5,
                        color: BRANDY
                      }}
                    >
                      Recommended
                    </Box>
                  )}
                </Box>
                <Box
                  component="button"
                  type="button"
                  aria-expanded={isOpen}
                  aria-label={isOpen ? `Hide what a ${f.title} involves` : `See what a ${f.title} involves`}
                  onClick={() => setExpanded(isOpen ? open.filter((k) => k !== f.key) : [...open, f.key])}
                  sx={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    bgcolor: 'transparent',
                    color: TEXT_2,
                    cursor: 'pointer',
                    '&:hover': { color: BRANDY }
                  }}
                >
                  {isOpen ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
                </Box>
              </Box>
              <Collapse in={isOpen} unmountOnExit>
                <Box sx={{ px: '12px', pb: '10px', pt: '2px', borderTop: `1px solid ${LINE}` }}>
                  {[
                    ['What It Is', f.job] as [string, string],
                    ...f.detail,
                    ...(isPick && pick ? [[WHY_PICKED, pick.reason] as [string, string]] : [])
                  ].map(([label, text]) => (
                    <Box key={label} sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', pt: '8px' }}>
                      <Typography
                        sx={{
                          ...LABEL,
                          width: 136,
                          flexShrink: 0,
                          lineHeight: 1.5,
                          // Every row title stays on its one line.
                          whiteSpace: 'nowrap',
                          color: label === WHY_PICKED ? BRANDY : TEXT_3
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography sx={{ ...BODY, flex: 1, fontSize: 12.5, lineHeight: 1.5 }}>{text}</Typography>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </FormRow>
  );

  // ---- Structure, Content, Members
  if (phase === 'build' && groupId) {
    return (
      <TrainingBuildSteps
        groupId={groupId}
        programName={details.name.trim()}
        formatTitle={formatInfo?.title ?? 'Course'}
        lessonTags={{
          capabilities: allCapabilities.filter((c) => picked.includes(c.id)).map((c) => ({ id: c.id, name: c.name })),
          outcomes: givenOutcomes
        }}
        // A program built before every program was for roles (for attributes or everyone, no roles)
        // keeps the Members step it was built for.
        roleBased={
          audienceRoleIds.length > 0
            ? { roles: roles.filter((r) => audienceRoleIds.includes(r.id)), roleNames: kept?.roleNames ?? roleNames }
            : undefined
        }
        published={Boolean(resume?.published)}
        onMoved={() => program.changed?.(groupId)}
        onFinished={() => onFinished?.(groupId)}
      />
    );
  }

  // ---- Details
  const detailsToGo = [...detailsMissing(details), ...(taken ? ['A name no other program uses'] : [])];
  if (step === 'details') {
    const bar = stepBar('Details', detailsToGo);
    return (
      <FlowStep
        stepper={stepper}
        bar={{
          ...bar,
          actions: (
            <>
              <ActionButton onClick={onClose}>Cancel</ActionButton>
              <ActionButton
                primary
                disabled={!bar.done || busy}
                reason={busy ? 'One moment.' : 'Fill in the required inputs first.'}
                onClick={leaveDetails}
              >
                {busy ? 'Saving...' : 'Next'}
              </ActionButton>
            </>
          )
        }}
      >
        <DetailsFields {...detailProps} />
      </FlowStep>
    );
  }

  // ---- Goals
  if (step === 'goals') {
    const bar = stepBar(
      'Goals',
      [!purpose.trim() && 'Purpose', givenOutcomes.length < outcomesNeeded && `${outcomesNeeded} Goals`].filter(
        (m): m is string => Boolean(m)
      )
    );
    return (
      <FlowStep
        stepper={stepper}
        bar={{
          ...bar,
          previous,
          actions: (
            <ActionButton
              primary
              disabled={!bar.done}
              reason="Fill in the required inputs first."
              onClick={() => go('format')}
            >
              Next
            </ActionButton>
          )
        }}
      >
        {purposeField}
        {capabilitiesField}
        {outcomesField}
      </FlowStep>
    );
  }

  // ---- Format
  if (step === 'format') {
    return (
      <FlowStep
        stepper={stepper}
        bar={{
          done: Boolean(formatInfo),
          title: formatInfo
            ? `${formatInfo.title} selected`
            : picking
            ? 'Elliott is picking a format'
            : 'Choose a format',
          previous,
          actions: (
            <ActionButton primary disabled={!formatInfo} reason="Choose a format first." onClick={() => go('review')}>
              Next
            </ActionButton>
          )
        }}
      >
        {formatField}
      </FlowStep>
    );
  }

  // ---- Review
  const lines: SummaryLine<ReviewKey>[] = [
    { key: 'name', label: 'Name', value: details.name.trim() || null },
    { key: 'stage', label: 'Stage', value: stageName },
    { key: 'category', label: 'Category', value: details.category ? CATEGORY_NAME[details.category] : null },
    { key: 'audience', label: 'Audience', value: who || null },
    {
      key: 'knowledge',
      label: 'Knowledge',
      value: details.docIds.length ? `${details.docIds.length} sources` : 'No sources'
    },
    { key: 'owner', label: 'Owner', value: ownerName },
    { key: 'owner', label: 'Publish by', value: formatDate(details.publishBy) ?? 'No date' },
    { key: 'purpose', label: 'Purpose', value: purpose.trim() || null },
    {
      key: 'capabilities' as const,
      label: 'Capabilities',
      value: pickedNames.length ? pickedNames.join(', ') : 'None: built on goals'
    },
    {
      key: 'outcomes',
      label: withCapabilities ? 'Other goals' : 'Goals',
      // Optional beside capabilities; otherwise three are needed, and fewer reads as missing.
      value:
        givenOutcomes.length >= Math.max(1, outcomesNeeded) ? givenOutcomes.join('; ') : outcomesNeeded ? null : 'None'
    },
    { key: 'format', label: 'Format', value: formatInfo ? `${formatInfo.title} (${formatInfo.length})` : null }
  ];
  const renderEditor = (key: ReviewKey) => {
    if (key === 'purpose') return purposeField;
    if (key === 'capabilities') return capabilitiesField;
    if (key === 'outcomes') return outcomesField;
    if (key === 'format') return formatField;
    const FieldComponent = DETAIL_FIELDS[key];
    return <FieldComponent {...detailProps} />;
  };
  // Anything edited here must still stand: the program is saved again from it before the build starts.
  const toGo = [
    ...detailsToGo,
    ...(purpose.trim() ? [] : ['Purpose']),
    ...(givenOutcomes.length < outcomesNeeded ? [`${outcomesNeeded} Goals`] : []),
    ...(formatInfo ? [] : ['Format'])
  ];
  const ready = toGo.length === 0 && !preview.writing;
  return (
    <FlowStep
      stepper={stepper}
      bar={{
        done: ready,
        title: preview.writing
          ? 'Elliott is writing the preview'
          : toGo.length > 0
          ? `${toGo.length} required ${toGo.length === 1 ? 'field' : 'fields'} left`
          : 'Ready to build',
        body: preview.writing
          ? undefined
          : toGo.length > 0
          ? toGo.join(', ')
          : 'Elliott builds the structure and lessons from this, for you to review.',
        previous,
        actions: (
          <ActionButton
            primary
            disabled={!ready || busy}
            reason={preview.writing || busy ? 'One moment.' : 'Fill in the required inputs first.'}
            onClick={generate}
          >
            {busy ? 'Starting...' : `Build ${formatInfo?.title ?? 'Course'} Structure`}
          </ActionButton>
        )
      }}
    >
      <ReviewFields preview={preview} lines={lines} renderEditor={renderEditor} />
    </FlowStep>
  );
};

/**
 * Publish Training. Opened on a program built here (`resume`), it reads what
 * is kept with the program first, so its steps open as they were left.
 */
const TrainingFlow: React.FC<TrainingFlowProps> = (props) => {
  const { resume, onClose, rolesLoading = false } = props;
  const resumeGroupId = resume?.groupId ?? null;
  const [kept, setKept] = useState<TrainingSnapshot | null>(null);
  // Audience Requirements with its backend on: the groups the program holds come with it. A read that
  // fails stops the reopen, so Details never starts its groups again over the ones kept.
  const groupsOnBackend = useRequirementsPilot() && audienceRequirementsBackend();
  const [heldGroups, setHeldGroups] = useState<AudienceGroupsDraft | null>(null);
  useEffect(() => {
    if (!resumeGroupId) return undefined;
    let alive = true;
    const groups = groupsOnBackend
      ? API.getAudienceRequirements({ id: resumeGroupId, ...getLLAuthHeaders() }).then(({ data }) =>
          data?.audienceGroups ? fromServerGroups(data.audienceGroups) : null
        )
      : Promise.resolve(null);
    Promise.all([openTraining(resumeGroupId), groups])
      .then(([snapshot, held]) => {
        if (!alive) return;
        if (!snapshot) throw new Error('This program was not built here.');
        setHeldGroups(held);
        setKept(snapshot);
      })
      .catch(() => {
        if (!alive) return;
        toast.error('This program could not be reopened. Please try again.');
        onClose();
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeGroupId]);
  // The roles too: the capabilities the program builds come from them, and a build started
  // without them would be saved as built on none.
  if ((resumeGroupId && !kept) || rolesLoading) {
    return (
      <Box
        role="status"
        aria-label={resumeGroupId ? 'Opening your program' : 'Loading the roles'}
        sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'center',
            gap: '24px',
            px: '24px',
            py: '14px',
            borderBottom: `1px solid ${LINE}`
          }}
        >
          {[0, 1, 2, 3].map((i) => (
            <Bone key={i} width={96} height={24} radius="12px" />
          ))}
        </Box>
        <Box
          sx={{
            maxWidth: 720,
            width: '100%',
            mx: 'auto',
            px: '24px',
            py: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '18px'
          }}
        >
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Bone width={140} height={14} radius="4px" />
              <Bone height={36} radius="4px" />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', ...fadeInSx }}>
      <TrainingSteps {...props} kept={kept} heldGroups={heldGroups} />
    </Box>
  );
};

export default TrainingFlow;
